import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';
import { Spinner } from 'react-bootstrap';

import { cn } from '@/lib/utils';

export const buttonVariants = cva('tw-flex tw-items-center tw-rounded-md tw-cursor-pointer  tw-justify-center tw-whitespace-nowrap tw-h-[38px] gap-2 ', {
  variants: {
    variant: {
      primary:
        " tw-text-white tw-border-none tw-self-stretch  tw-relative tw-bg-primary  tw-shadow-sm tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight",
      info: "tw-bg-white tw-border-none tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight",
      danger:
        "tw-text-white tw-border-none tw-self-stretch  tw-relative tw-bg-red-600  tw-shadow-sm tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight",
      outlineDanger:
        "hover:tw-bg-red-600 hover:tw-text-white tw-text-red-600 tw-bg-white tw-border tw-border-solid tw-self-stretch  tw-relative tw-border-red-600 tw-shadow-sm tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight",
      disabled:
        "tw-text-white tw-border-none tw-self-stretch  tw-relative tw-bg-gray-400  tw-shadow-sm tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight",
      warning:
        "tw-text-white tw-border-none tw-self-stretch  tw-relative tw-bg-orange-400  tw-shadow-sm tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight",
      outlineWarning:
        "hover:tw-bg-orange-400 hover:tw-text-white tw-text-orange-400 tw-bg-white tw-border tw-border-solid tw-self-stretch  tw-relative tw-border-orange-400  tw-shadow-sm tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight",
      outline:
        "hover:tw-bg-primary hover:tw-text-white tw-text-blue-600 tw-bg-white tw-border tw-border-solid tw-self-stretch  tw-relative tw-border-blue-600  tw-shadow-sm tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight",
      secondary: " tw-text-blue-800 tw-border-none tw-self-stretch  tw-relative tw-bg-blue-100 tw-text-sm tw-font-normal tw-shadow-sm tw-font-['Lexend']",
      ghost: ' hover:bg-accent hover:text-accent-foreground',
      link: ' text-primary underline-offset-4 hover:underline',
      gray: ' p-0 tw-no-underline tw-text-gray-400 hover:!tw-text-gray-500 active:!tw-text-gray-300 btn btn-link',
      none: '',
    },
    size: {
      default: 'tw-h-9 tw-px-4 tw-py-2',
      sm: 'tw-h-8  px-3 tw-text-xs',
      lg: 'tw-h-10  tw-px-8',
      icon: 'tw-h-9 tw-w-9',
      none: '',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'default',
  },
});

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  title?: string;
  icon?: ButtonProps & React.RefAttributes<HTMLButtonElement>;
  loadingAnimation?: 'grow' | 'border';
  id: string;
}

const PSButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ disabled, className, variant, size, asChild = false, isLoading, title, children, icon, loadingAnimation = 'border', id, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    if (disabled) {
      variant = 'disabled';
    }
    return (
      <Comp
        id={id}
        disabled={disabled}
        className={cn(
          buttonVariants({
            variant,
            size,
            className: `${className ?? ''}` + `${disabled ? ' tw-bg-gray-400 tw-text-white ' : ''}`,
          })
        )}
        ref={ref}
        {...props}
      >
        {isLoading ? (
          <Spinner animation={loadingAnimation} size="sm" />
        ) : (
          <>
            <>{icon}</>
            <>{children}</>
            {title && <span>{title}</span>}
          </>
        )}
      </Comp>
    );
  }
);
PSButton.displayName = 'Button';

export { PSButton };
